import React from 'react';
import PropTypes from 'prop-types';
import { localize } from 'core/localization';
import ResultsBlock from '../ResultsBlock/ResultsBlock';
import Header from '../Header/Header';
import SectionTreeNode from '../SectionTreeNode/SectionTreeNode';
import QuestionTreeNode from '../QuestionTreeNode/QuestionTreeNode';
import LearningObjectiveTreeNode from '../LearningObjectiveTreeNode/LearningObjectiveTreeNode';

import { Sections, ExpandedTocContainer } from './ExpandedToc.styled';

function ExpandedTocSection({ props }) {
  const {
    courseStructure,
    activeSectionId,
    activeQuestionId,
    navigateToQuestion,
    navigateToLearningObjective,
    isExpanded,
    isLearningObjectiveView
  } = props;
  return courseStructure.sections.map((section, sectionIndex) => (
    <SectionTreeNode
      key={sectionIndex}
      isActive={section.id === activeSectionId}
      isPassed={section.isPassed}
      score={section.score}
      questions={section.questions}
      affectsProgress={section.affectsProgress}
      title={section.title}
      tabIndex={isExpanded ? 0 : -1}
    >
      {section.learningObjective && (
        <LearningObjectiveTreeNode
          role="menuitem"
          tabIndex={1}
          isActive={section.id === activeSectionId && isLearningObjectiveView}
          onClick={() => navigateToLearningObjective(section.id)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              navigateToLearningObjective(section.id);
            }
          }}
        />
      )}
      <ExpandedTocItems props={{ section, activeQuestionId, isExpanded, navigateToQuestion }} />
    </SectionTreeNode>
  ));
}

function ExpandedTocItems({ props }) {
  const { activeQuestionId, section, navigateToQuestion, isExpanded } = props;
  return props.section.questions.map((question, questionIndex) => (
    <QuestionTreeNode
      role="menuitem"
      key={questionIndex}
      question={question}
      isActive={question.id === activeQuestionId}
      tabIndex={isExpanded ? 0 : -1}
      {...question.id !== activeQuestionId && {
        onClick: () => navigateToQuestion(section.id, question.id),
        onKeyDown: event => {
          if (event.key === 'Enter') {
            navigateToQuestion(section.id, question.id);
          }
        }
      }}
    />
  ));
}

const ExpandedToc = ({
  courseStructure,
  activeSectionId,
  activeQuestionId,
  navigateToIndex,
  navigateToResults,
  navigateToQuestion,
  navigateToLearningObjective,
  isResultsActive,
  isExpanded,
  isLearningObjectiveView
}) => (
  <React.Fragment>
    <ExpandedTocContainer
      isExpanded={isExpanded}
      aria-hidden={!isExpanded}
      ariaLabel={`${localize('[expanded nav section]')}`}
    >
      <Header
        courseTitle={courseStructure.title}
        navigateToIndex={navigateToIndex}
        tabIndex={isExpanded ? 0 : -1}
      />
      <Sections role="menubar">
        <ExpandedTocSection
          props={{
            courseStructure,
            activeSectionId,
            activeQuestionId,
            navigateToIndex,
            navigateToResults,
            navigateToQuestion,
            navigateToLearningObjective,
            isResultsActive,
            isExpanded,
            isLearningObjectiveView
          }}
        />
      </Sections>
      <ResultsBlock
        ariaLabel={localize('[results]')}
        isActive={isResultsActive}
        navigateToResults={navigateToResults}
        tabIndex={isExpanded ? 0 : -1}
      />
    </ExpandedTocContainer>
  </React.Fragment>
);

ExpandedToc.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  courseStructure: PropTypes.shape({
    title: PropTypes.string.isRequired,
    sections: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired
  }).isRequired,
  activeSectionId: PropTypes.string,
  activeQuestionId: PropTypes.string,
  navigateToIndex: PropTypes.func.isRequired,
  navigateToQuestion: PropTypes.func.isRequired,
  navigateToLearningObjective: PropTypes.func.isRequired,
  navigateToResults: PropTypes.func.isRequired,
  isResultsActive: PropTypes.bool.isRequired,
  isLearningObjectiveView: PropTypes.bool.isRequired
};

export default ExpandedToc;
